'use client';

/**
 * AppsFlyer integration (native-only).
 *
 * Architecture note: this app is a Next.js site loaded *remotely* into a
 * Capacitor WebView (see capacitor.config.ts `server.url`). The AppsFlyer
 * SDK runs natively on the device; this module talks to it through the
 * `appsflyer-capacitor-plugin` bridge. On the plain web build every call
 * here is a no-op, so it is always safe to import.
 *
 * What it does:
 *  - Initializes the SDK once, registering the Unified Deep Linking (UDL)
 *    listener BEFORE start so both *direct* (app already installed) and
 *    *deferred* (resolved right after a fresh install) deep links are caught.
 *  - Resolves an incoming OneLink into an in-app route and hands it to a
 *    navigation callback supplied by the React layer, so the destination
 *    opens *inside the app* instead of bouncing to the browser.
 *  - Feeds the Firebase (FCM) token to AppsFlyer so Android uninstall
 *    measurement works.
 *
 * Dashboard / secrets setup lives in APPSFLYER_SETUP.md.
 */

import { Capacitor } from '@capacitor/core';
import { getFcmToken } from '@/lib/firebase/messaging';

// AppsFlyer credentials. NEXT_PUBLIC_* so they are available in the WebView
// bundle. The dev key is not a secret (it ships in every mobile binary) —
// it only identifies the app to AppsFlyer's attribution endpoint.
//
// Normally the dev key is account-level (one key for all platforms). But if the
// Android and iOS apps live in *different* AppsFlyer accounts, each has its own
// key — so we support optional per-platform overrides, falling back to the
// shared key. Each var is referenced statically so Next.js inlines it at build.
const DEV_KEY_SHARED = process.env.NEXT_PUBLIC_APPSFLYER_DEV_KEY || '';
const DEV_KEY_ANDROID = process.env.NEXT_PUBLIC_APPSFLYER_DEV_KEY_ANDROID || '';
const DEV_KEY_IOS = process.env.NEXT_PUBLIC_APPSFLYER_DEV_KEY_IOS || '';

function devKeyForPlatform(): string {
  const platform = Capacitor.getPlatform();
  if (platform === 'android') return DEV_KEY_ANDROID || DEV_KEY_SHARED;
  if (platform === 'ios') return DEV_KEY_IOS || DEV_KEY_SHARED;
  return DEV_KEY_SHARED;
}
// iOS App Store numeric ID, e.g. "1234567890". Blank until the iOS app
// exists in AppsFlyer; Android ignores this field.
const IOS_APP_ID = process.env.NEXT_PUBLIC_APPSFLYER_IOS_APP_ID || '';
// Turn on verbose SDK logging in dev/staging only.
const IS_DEBUG = process.env.NEXT_PUBLIC_APPSFLYER_DEBUG === 'true';

// The OneLink template base, e.g. "https://docupro.onelink.me/qagF". We parse
// out the brand domain ("docupro.onelink.me") and the template id ("qagF"):
// both are needed to ask the SDK to mint *short* invite links.
const ONELINK_URL = process.env.NEXT_PUBLIC_APPSFLYER_ONELINK || '';
function parseOneLink(): { domain: string; templateId: string } | null {
  try {
    const u = new URL(ONELINK_URL);
    const templateId = u.pathname.replace(/^\/+/, '').split('/')[0];
    if (!templateId) return null;
    return { domain: u.host, templateId };
  } catch {
    return null;
  }
}

/**
 * A function the React layer registers to perform in-app navigation
 * (typically `router.push`). Stored at module scope because a deferred deep
 * link can arrive before any component finishes mounting.
 */
type Navigate = (path: string) => void;
let navigate: Navigate | null = null;
// If a deep link resolves before the navigator is registered, park it here
// and replay it the moment `setDeepLinkNavigator` is called.
let pendingPath: string | null = null;

let initialized = false;

export function setDeepLinkNavigator(fn: Navigate): void {
  navigate = fn;
  if (pendingPath) {
    const path = pendingPath;
    pendingPath = null;
    fn(path);
  }
}

export function clearDeepLinkNavigator(): void {
  navigate = null;
}

function go(path: string): void {
  if (navigate) navigate(path);
  else pendingPath = path; // replayed once a navigator registers
}

/**
 * Maps an AppsFlyer deep link object into an in-app route.
 *
 * Convention (configured on the OneLink template in the dashboard):
 *  - `deep_link_value = "referral"` (+ `deep_link_sub1 = <CODE>`) →
 *    `/signup?referral=CODE` so the signup form pre-fills the referral code.
 *    (`referral`, not `ref` — `ref` is reserved for marketing/source tags.)
 *  - `deep_link_value` starting with "/" is treated as a literal in-app
 *    path, e.g. `/categories/passport` or a full city/category/service path.
 *  - anything else is treated as a single path segment.
 *
 * Returns null when there's nothing actionable (caller falls back to home).
 */
export function resolveDeepLinkPath(deepLink: Record<string, any> | null | undefined): string | null {
  if (!deepLink) return null;

  const value: string | undefined =
    deepLink.deep_link_value ?? deepLink.deepLinkValue ?? deepLink.af_dp;

  // Referral / invite flow.
  if (value === 'referral' || value === 'invite') {
    const code: string | undefined =
      deepLink.deep_link_sub1 ?? deepLink.referrer_code ?? deepLink.ref ?? deepLink.af_sub1;
    return code ? `/signup?referral=${encodeURIComponent(code)}` : '/signup';
  }

  if (typeof value === 'string' && value.length > 0) {
    const path = value.startsWith('/') ? value : `/${value}`;
    // "//host" and "/\host" are protocol-relative URLs — they navigate
    // off-origin despite starting with "/". Relative in-app paths only.
    if (/^\/[/\\]/.test(path)) return null;
    return path;
  }

  return null;
}

/**
 * Initializes the AppsFlyer SDK exactly once. No-op on web and on repeat
 * calls. Safe to await from app startup.
 */
export async function initAppsFlyer(): Promise<void> {
  if (initialized) return;
  if (!Capacitor.isNativePlatform()) return;
  const devKey = devKeyForPlatform();
  if (!devKey) {
    console.warn(
      `[appsflyer] no dev key configured for platform "${Capacitor.getPlatform()}" — skipping init`
    );
    return;
  }
  initialized = true;

  try {
    const { AppsFlyer, AFConstants } = await import('appsflyer-capacitor-plugin');

    // Register listeners BEFORE startSDK so deferred deep links (resolved
    // right after a fresh install) are not missed.
    await AppsFlyer.addListener(AFConstants.UDL_CALLBACK, (event) => {
      // event.status is "FOUND" | "NOT_FOUND" | "ERROR".
      if (event.status === 'FOUND') {
        const path = resolveDeepLinkPath(event.deepLink);
        if (path) go(path);
      } else if (event.error) {
        console.warn('[appsflyer] UDL error', event.error);
      }
    });

    // Conversion data — primarily for analytics / install-source insight.
    await AppsFlyer.addListener(AFConstants.CONVERSION_CALLBACK, (event) => {
      if (event.callbackName === AFConstants.onConversionDataFail) {
        console.warn('[appsflyer] conversion data failed', event.errorMessage);
      }
      // Routing is handled by UDL above; conversion data is left for future
      // attribution-based logic (e.g. tagging the user's acquisition source).
    });

    await AppsFlyer.initSDK({
      devKey,
      appID: IOS_APP_ID, // iOS only; ignored on Android
      isDebug: IS_DEBUG,
      registerConversionListener: true,
      registerOnDeepLink: true,
      registerOnAppOpenAttribution: false,
      // Start manually so the listeners above are guaranteed to be attached
      // before the first session is reported.
      manualStart: true,
    });

    await AppsFlyer.startSDK();

    // Register the OneLink template id with the SDK (used by AppsFlyer's
    // attribution/deep-link resolution for invite links).
    const ol = parseOneLink();
    if (ol) {
      try {
        await AppsFlyer.setAppInviteOneLink({ onelinkID: ol.templateId });
      } catch (err) {
        console.warn('[appsflyer] setAppInviteOneLink failed', err);
      }
    }

    // Debug aid: log the AppsFlyer UID ("device id") so it can be copied into
    // the dashboard's Test devices list. Gated behind the debug flag so it
    // never prints in production.
    if (IS_DEBUG) {
      try {
        const { uid } = await AppsFlyer.getAppsFlyerUID();
        console.log('[appsflyer] device id (AppsFlyer UID):', uid);
      } catch {
        /* non-fatal */
      }
    }

    // Android uninstall measurement: hand AppsFlyer the FCM token. iOS
    // uninstall uses silent push handled by the SDK natively, so this is
    // Android-specific. Best-effort — never block startup on it.
    if (Capacitor.getPlatform() === 'android') {
      void registerUninstallToken(AppsFlyer);
    }
  } catch (err) {
    initialized = false; // allow a later retry
    console.error('[appsflyer] init failed', err);
  }
}

async function registerUninstallToken(appsFlyer: {
  updateServerUninstallToken: (t: { token: string }) => Promise<void>;
}): Promise<void> {
  try {
    const token = await getFcmToken();
    if (token) await appsFlyer.updateServerUninstallToken({ token });
  } catch (err) {
    console.warn('[appsflyer] uninstall token registration failed', err);
  }
}

/**
 * Associates the logged-in user with their AppsFlyer events so referral
 * attribution and analytics can be tied to a real account. Call after login.
 */
export async function setAppsFlyerUserId(userId: string): Promise<void> {
  if (!initialized || !Capacitor.isNativePlatform()) return;
  try {
    const { AppsFlyer } = await import('appsflyer-capacitor-plugin');
    await AppsFlyer.setCustomerUserId({ cuid: userId });
  } catch (err) {
    console.warn('[appsflyer] setCustomerUserId failed', err);
  }
}

/**
 * Returns the **AppsFlyer UID** (`appsflyer_id`) — AppsFlyer's unique
 * per-install device identifier. This is the "device id" you use to match a
 * device in the AppsFlyer dashboard, raw-data / Pull API exports, S2S events,
 * and the deep-link/attribution APIs. It is created on first launch and is
 * stable for the lifetime of the install (a reinstall yields a new one).
 *
 * Returns null on web or before the SDK has initialized. Safe to call once
 * `initAppsFlyer()` has run (e.g. after login, or alongside it).
 */
export async function getAppsFlyerDeviceId(): Promise<string | null> {
  if (!initialized || !Capacitor.isNativePlatform()) return null;
  try {
    const { AppsFlyer } = await import('appsflyer-capacitor-plugin');
    const { uid } = await AppsFlyer.getAppsFlyerUID();
    return uid ?? null;
  } catch (err) {
    console.warn('[appsflyer] getAppsFlyerUID failed', err);
    return null;
  }
}

// Referral invite links are built as short, query-trimmed OneLinks by
// buildReferralOneLink() in src/lib/onelink.ts (no paid shortlink API needed).

/**
 * Logs an in-app event to AppsFlyer (for funnel / conversion analytics).
 * No-op on web. `eventValue` is an arbitrary key/value bag.
 */
export async function logAppsFlyerEvent(
  eventName: string,
  eventValue: Record<string, any> = {}
): Promise<void> {
  if (!initialized || !Capacitor.isNativePlatform()) return;
  try {
    const { AppsFlyer } = await import('appsflyer-capacitor-plugin');
    await AppsFlyer.logEvent({ eventName, eventValue });
  } catch (err) {
    console.warn('[appsflyer] logEvent failed', err);
  }
}
